const DEFAULT_SOURCE = 'Manual';
const DEFAULT_NOTE = 'Temporary';

const toArray = (value) => Array.isArray(value) ? value : [];
const ITEM_EQUIP_MODES = new Set(['equip', 'item-equip']);

export const buildLegacyEffectOccurrenceKey = (text, occurrence) => (
  !occurrence || occurrence <= 0 ? text : `${text}::${occurrence}`
);

const countInArrays = (text, ...arrays) => {
  let count = 0;
  arrays.forEach((arr) => {
    toArray(arr).forEach((entry) => {
      if (entry === text) count += 1;
    });
  });
  return count;
};

export const getLegacyEffectGlobalOccurrence = (text, index, list, ...priorArrays) => {
  let count = countInArrays(text, ...priorArrays);
  const safeList = toArray(list);
  for (let i = 0; i < index; i += 1) {
    if (safeList[i] === text) count += 1;
  }
  return count;
};

export const lookupLegacyEffectData = (text, occurrence, buffData = {}) => {
  if (!buffData) return undefined;
  const key = buildLegacyEffectOccurrenceKey(text, occurrence);
  return buffData[key];
};

const EFFECT_SECTIONS = [
  { listKey: 'permBuffs', category: 'buff', permanence: 'permanent' },
  { listKey: 'buffList', category: 'buff', permanence: 'temporary' },
  { listKey: 'permDebuffs', category: 'debuff', permanence: 'permanent' },
  { listKey: 'debuffList', category: 'debuff', permanence: 'temporary' },
];

const normalizeLegacyEffectProvenance = (effectData = {}, source = DEFAULT_SOURCE) => {
  const raw = effectData?.provenance && typeof effectData.provenance === 'object'
    ? effectData.provenance
    : null;
  const sourceType = raw?.sourceType || (effectData?.sourceItemId || effectData?.sourceItemEntryId ? 'item' : null);
  if (sourceType !== 'item') return raw || null;

  return {
    ...raw,
    sourceType: 'item',
    itemEntryId: raw?.itemEntryId || effectData.sourceItemEntryId || effectData.sourceItemId || null,
    itemId: raw?.itemId || effectData.sourceItemId || effectData.sourceItemEntryId || null,
    itemDefinitionId: raw?.itemDefinitionId || raw?.definitionId || effectData.sourceItemDefinitionId || null,
    itemName: raw?.itemName || source || null,
    mode: raw?.mode || effectData.addedBy || null,
    abilityId: raw?.abilityId || effectData.sourceAbilityId || null,
    abilityName: raw?.abilityName || effectData.sourceAbilityName || null,
  };
};

const normalizeRuntimeEffectProvenance = (runtime = {}, source = DEFAULT_SOURCE) => {
  const raw = runtime?.raw && typeof runtime.raw === 'object' ? runtime.raw : {};
  const existing = runtime?.provenance && typeof runtime.provenance === 'object'
    ? runtime.provenance
    : raw.provenance && typeof raw.provenance === 'object'
      ? raw.provenance
      : null;
  const sourceType = existing?.sourceType || runtime?.sourceType || raw.sourceType || null;
  if (sourceType !== 'item') return existing || null;

  return {
    ...existing,
    sourceType: 'item',
    itemEntryId:
      existing?.itemEntryId
      || runtime.itemEntryId
      || runtime.sourceItemEntryId
      || raw.itemEntryId
      || raw.sourceItemEntryId
      || runtime.sourceItemId
      || raw.sourceItemId
      || null,
    itemId:
      existing?.itemId
      || runtime.itemId
      || raw.itemId
      || runtime.sourceItemId
      || raw.sourceItemId
      || runtime.itemEntryId
      || raw.itemEntryId
      || null,
    itemDefinitionId:
      existing?.itemDefinitionId
      || existing?.definitionId
      || runtime.itemDefinitionId
      || runtime.sourceItemDefinitionId
      || raw.itemDefinitionId
      || raw.sourceItemDefinitionId
      || null,
    itemName:
      existing?.itemName
      || runtime.itemName
      || raw.itemName
      || source
      || null,
    mode:
      existing?.mode
      || runtime.sourceMode
      || runtime.addedBy
      || raw.sourceMode
      || raw.addedBy
      || null,
    abilityId:
      existing?.abilityId
      || runtime.abilityId
      || runtime.sourceAbilityId
      || raw.abilityId
      || raw.sourceAbilityId
      || null,
    abilityName:
      existing?.abilityName
      || runtime.abilityName
      || runtime.sourceAbilityName
      || raw.abilityName
      || raw.sourceAbilityName
      || null,
  };
};

export const buildLegacyEffectEntries = ({
  buffList = [],
  debuffList = [],
  permBuffs = [],
  permDebuffs = [],
  buffData = {},
  buffSources = {},
  buffNotes = {},
  disabledBuffs = [],
  disabledDebuffs = [],
  hiddenBuffs = [],
  hiddenDebuffs = [],
} = {}) => {
  const sourceMap = buffSources || {};
  const noteMap = buffNotes || {};
  const dataMap = buffData || {};
  const disabledBuffKeys = new Set(toArray(disabledBuffs));
  const disabledDebuffKeys = new Set(toArray(disabledDebuffs));
  const hiddenBuffKeys = new Set(toArray(hiddenBuffs));
  const hiddenDebuffKeys = new Set(toArray(hiddenDebuffs));
  const lists = { buffList, debuffList, permBuffs, permDebuffs };
  const priorArrays = [];
  const entries = [];

  EFFECT_SECTIONS.forEach(({ listKey, category, permanence }) => {
    const list = toArray(lists[listKey]);
    list.forEach((text, listIndex) => {
      const occurrence = getLegacyEffectGlobalOccurrence(text, listIndex, list, ...priorArrays);
      const legacyKey = buildLegacyEffectOccurrenceKey(text, occurrence);
      const effectData = lookupLegacyEffectData(text, occurrence, dataMap) || {};
      const fallbackNote = permanence === 'permanent' ? 'Permanent' : 'Temporary';
      const source = effectData.source || (occurrence === 0 ? sourceMap[text] : null) || DEFAULT_SOURCE;
      const note = effectData.note || noteMap[text] || fallbackNote;
      const provenance = normalizeLegacyEffectProvenance(effectData, source);
      const sourceType = provenance?.sourceType === 'item' ? 'item' : 'legacy';
      const disabledSet = category === 'buff' ? disabledBuffKeys : disabledDebuffKeys;
      const hiddenSet = category === 'buff' ? hiddenBuffKeys : hiddenDebuffKeys;
      const isDisabled = disabledSet.has(legacyKey);
      const isHidden = hiddenSet.has(legacyKey);

      entries.push({
        effectId: `legacy:${listKey}:${legacyKey}`,
        effectInstanceId: effectData.effectInstanceId || null,
        effectGroupId: `legacy-group:${listKey}:${legacyKey}`,
        text,
        legacyKey,
        occurrence,
        listKey,
        listIndex,
        category,
        permanence,
        source,
        note,
        status: isDisabled ? 'disabled' : 'active',
        isDisabled,
        isHidden,
        display: {
          label: effectData.displayName || text,
          playerTooltip: effectData.playerTooltip || '',
          colorId: effectData.color || null,
          noteVisible: effectData.noteVisible === true,
          hidden: isHidden || effectData.hidden === true,
          glow: effectData.glow === true,
          effectType: effectData.effectType || null,
        },
        data: effectData,
        sourceType,
        storageSourceType: 'legacy',
        provenance,
        itemEntryId: provenance?.itemEntryId || null,
        itemId: provenance?.itemId || null,
        itemDefinitionId: provenance?.itemDefinitionId || null,
        itemName: provenance?.itemName || null,
        abilityId: provenance?.abilityId || null,
        abilityName: provenance?.abilityName || null,
        sourceMode: provenance?.mode || effectData.addedBy || null,
      });
    });
    priorArrays.push(list);
  });

  return entries;
};

const normalizeEffectTextKey = (value) => String(value || '').trim().toLowerCase();

const getEffectMode = (effect = {}) => String(
  effect.sourceMode
  || effect.provenance?.mode
  || effect.data?.addedBy
  || '',
).toLowerCase();

const getEffectItemName = (effect = {}) => (
  effect.itemName
  || effect.provenance?.itemName
  || effect.data?.provenance?.itemName
  || effect.data?.source
  || effect.source
  || ''
);

const getEffectItemKey = (effect = {}) => {
  const explicitId =
    effect.itemEntryId
    || effect.data?.sourceItemEntryId
    || effect.provenance?.itemEntryId
    || effect.data?.provenance?.itemEntryId
    || effect.itemId
    || effect.data?.sourceItemId
    || effect.provenance?.itemId
    || effect.data?.provenance?.itemId
    || null;

  if (explicitId) return `id:${String(explicitId)}`;

  const name = normalizeEffectTextKey(getEffectItemName(effect));
  return name ? `name:${name}` : null;
};

const isExplicitEquippedItemEffect = (effect = {}) => {
  if (!ITEM_EQUIP_MODES.has(getEffectMode(effect))) return false;
  return effect.sourceType === 'item'
    || effect.provenance?.sourceType === 'item'
    || effect.data?.provenance?.sourceType === 'item'
    || !!effect.itemEntryId
    || !!effect.itemId
    || !!effect.data?.sourceItemId
    || !!effect.data?.sourceItemEntryId;
};

const getEffectSourceNameKey = (effect = {}) => {
  const name = normalizeEffectTextKey(getEffectItemName(effect));
  return name ? `name:${name}` : null;
};

const getEffectIdentityKey = (effect = {}, itemKey) => [
  effect.category || '',
  normalizeEffectTextKey(effect.text || effect.name),
  itemKey || '',
].join('|');

export const dedupeLegacyEquippedItemEffects = (effects = []) => {
  const explicitNameKeys = new Set();
  toArray(effects).forEach((effect) => {
    if (!isExplicitEquippedItemEffect(effect)) return;
    const sourceNameKey = getEffectSourceNameKey(effect);
    if (!sourceNameKey) return;
    explicitNameKeys.add(getEffectIdentityKey(effect, sourceNameKey));
  });

  const seenExplicit = new Set();
  const deduped = [];

  toArray(effects).forEach((effect) => {
    const explicit = isExplicitEquippedItemEffect(effect);
    if (explicit) {
      const key = getEffectIdentityKey(effect, getEffectItemKey(effect));
      if (seenExplicit.has(key)) return;
      seenExplicit.add(key);
      deduped.push(effect);
      return;
    }

    const sourceNameKey = getEffectSourceNameKey(effect);
    if (sourceNameKey && explicitNameKeys.has(getEffectIdentityKey(effect, sourceNameKey))) {
      return;
    }

    deduped.push(effect);
  });

  return deduped;
};

const getRuntimeDurationRemaining = (duration, roundApplied, combatRound) => {
  if (duration === 'concentration') return null;
  if (typeof duration !== 'number') return null;
  if (typeof combatRound !== 'number') return duration;
  return Math.max(0, duration - (combatRound - (roundApplied || combatRound)));
};

const buildRuntimeEffectId = ({
  effectId,
  kind,
  targetId,
  casterId,
  name,
  roundApplied,
  index,
}) => (
  effectId
  || `runtime:${kind}:${targetId || 'unknown'}:${casterId || 'none'}:${name || 'effect'}:${roundApplied || 0}:${index ?? 0}`
);

const buildRuntimeEffectGroupId = ({
  effectGroupId,
  kind,
  targetId,
  casterId,
  name,
  roundApplied,
}) => (
  effectGroupId
  || `runtime-group:${kind}:${targetId || 'unknown'}:${casterId || 'none'}:${name || 'effect'}:${roundApplied || 0}`
);

const getConditionLabel = (condition) => {
  if (typeof condition === 'string') return condition;
  if (condition && typeof condition.name === 'string') return condition.name;
  return '';
};

const createRuntimeEntry = ({
  kind,
  category,
  name,
  targetId,
  targetName,
  targetType,
  casterId = null,
  casterName = null,
  duration = null,
  roundApplied = null,
  combatRound = null,
  concentration = false,
  effectId = null,
  effectGroupId = null,
  source = DEFAULT_SOURCE,
  note = DEFAULT_NOTE,
  permanence = 'temporary',
  index = 0,
  runtime = {},
}) => {
  const provenance = normalizeRuntimeEffectProvenance(runtime, source);
  const sourceType = provenance?.sourceType || runtime.sourceType || runtime.raw?.sourceType || kind;

  return {
    effectId: buildRuntimeEffectId({ effectId, kind, targetId, casterId, name, roundApplied, index }),
    effectGroupId: buildRuntimeEffectGroupId({ effectGroupId, kind, targetId, casterId, name, roundApplied }),
    sourceType,
    storageSourceType: 'runtime',
    category,
    permanence,
    status: 'active',
    isDisabled: false,
    text: name,
    display: {
      label: name,
      playerTooltip: '',
      colorId: null,
      noteVisible: true,
      hidden: false,
      glow: false,
      effectType: category,
    },
    data: runtime.raw || null,
    source,
    note,
    targetId,
    targetName,
    targetType,
    casterId,
    casterName,
    duration,
    roundApplied,
    remaining: getRuntimeDurationRemaining(duration, roundApplied, combatRound),
    concentration,
    ...runtime,
    provenance,
    itemEntryId: provenance?.itemEntryId || null,
    itemId: provenance?.itemId || null,
    itemDefinitionId: provenance?.itemDefinitionId || null,
    itemName: provenance?.itemName || null,
    abilityId: provenance?.abilityId || null,
    abilityName: provenance?.abilityName || null,
    sourceMode: provenance?.mode || runtime.sourceMode || runtime.raw?.sourceMode || runtime.raw?.addedBy || null,
  };
};

export const getCanonicalEffects = ({
  char = null,
  combatant = null,
  combatants = [],
  combatRound = null,
  legacyState = null,
  includeLegacy = true,
  includeRuntime = true,
  includeConditions = true,
  includeConcentration = true,
  includeMarks = true,
} = {}) => {
  const canonicalEffects = [];

  if (includeLegacy && char) {
    const legacyOptions = legacyState || {
      buffList: char.buffList,
      debuffList: char.debuffList,
      permBuffs: char.permanentBuffs,
      permDebuffs: char.permanentDebuffs,
      buffData: char.buffData,
      buffSources: char.buffSources,
      buffNotes: char.buffNotes,
      disabledBuffs: char.disabledBuffs,
      disabledDebuffs: char.disabledDebuffs,
      hiddenBuffs: char.hiddenBuffs,
      hiddenDebuffs: char.hiddenDebuffs,
    };
    canonicalEffects.push(...buildLegacyEffectEntries(legacyOptions));
  }

  if (includeRuntime && combatant) {
    toArray(combatant.debuffs).forEach((debuff, index) => {
      canonicalEffects.push(createRuntimeEntry({
        kind: 'combat-debuff',
        category: 'debuff',
        name: debuff.name,
        targetId: combatant.id,
        targetName: combatant.name,
        targetType: combatant.type,
        casterId: debuff.casterId || null,
        casterName: debuff.casterName || 'Unknown',
        duration: debuff.duration,
        roundApplied: debuff.roundApplied || null,
        combatRound,
        concentration: debuff.duration === 'concentration',
        effectId: debuff.id || debuff.effectId || null,
        effectGroupId: debuff.effectGroupId || null,
        source: debuff.casterName || 'Combat',
        note: 'Combat debuff',
        index,
        runtime: {
          bonusDice: debuff.bonusDice,
          percentIncrease: debuff.percentIncrease,
          flatBonus: debuff.flatBonus,
          dotDamage: debuff.dotDamage,
          dotDamageType: debuff.dotDamageType,
          hotHeal: debuff.hotHeal,
          hotTempHp: debuff.hotTempHp,
          casterOnly: debuff.casterOnly,
          saveType: debuff.saveType,
          saveDC: debuff.saveDC,
          saveTiming: debuff.saveTiming,
          saveRemovesEffect: debuff.saveRemovesEffect,
          raw: debuff,
        },
      }));
    });

    if (includeConditions) {
      toArray(combatant.conditions).forEach((condition, index) => {
        const label = getConditionLabel(condition);
        if (!label) return;
        canonicalEffects.push(createRuntimeEntry({
          kind: 'combat-condition',
          category: 'condition',
          name: label,
          targetId: combatant.id,
          targetName: combatant.name,
          targetType: combatant.type,
          duration: null,
          concentration: false,
          effectId: condition?.id || condition?.effectId || null,
          effectGroupId: condition?.effectGroupId || null,
          source: 'Combat',
          note: 'Condition',
          index,
          runtime: {
            raw: condition,
          },
        }));
      });
    }
  }

  if (includeRuntime && char && combatant) {
    toArray(char.activeEffects).forEach((effect, index) => {
      canonicalEffects.push(createRuntimeEntry({
        kind: 'active-effect',
        category: 'buff',
        name: effect.name,
        targetId: char.id,
        targetName: char.name || combatant.name,
        targetType: combatant.type,
        casterId: effect.casterId || char.id,
        casterName: effect.casterName || char.name || combatant.name,
        duration: effect.duration,
        roundApplied: effect.roundApplied || null,
        combatRound,
        concentration: effect.duration === 'concentration',
        effectId: effect.id || effect.effectId || null,
        effectGroupId: effect.effectGroupId || null,
        source: effect.source || effect.casterName || char.name || DEFAULT_SOURCE,
        note: 'Active effect',
        index,
        runtime: {
          modifier: effect.modifier,
          autoApply: effect.autoApply,
          dotDamage: effect.dotDamage,
          dotDamageType: effect.dotDamageType,
          hotHeal: effect.hotHeal,
          hotTempHp: effect.hotTempHp,
          saveType: effect.saveType,
          saveDC: effect.saveDC,
          saveTiming: effect.saveTiming,
          saveRemovesEffect: effect.saveRemovesEffect,
          casterOnly: effect.casterOnly,
          bonusDice: effect.bonusDice,
          percentIncrease: effect.percentIncrease,
          flatBonus: effect.flatBonus,
          raw: effect,
        },
      }));
    });

    if (includeConcentration && char.concentratingOn) {
      canonicalEffects.push(createRuntimeEntry({
        kind: 'concentration',
        category: 'concentration',
        name: char.concentratingOn,
        targetId: char.id,
        targetName: char.name || combatant.name,
        targetType: combatant.type,
        casterId: char.id,
        casterName: char.name || combatant.name,
        duration: 'concentration',
        concentration: true,
        effectGroupId: char.concentrationEffectGroupId || null,
        source: char.name || DEFAULT_SOURCE,
        note: 'Concentration',
      }));
    }

    if (includeMarks) {
      toArray(char.markedTargets).forEach((mark, index) => {
        const targetCombatant = toArray(combatants).find((candidate) => candidate.id === mark.targetId);
        canonicalEffects.push(createRuntimeEntry({
          kind: 'mark',
          category: 'mark',
          name: mark.spellName || 'Mark',
          targetId: mark.targetId,
          targetName: mark.targetName || targetCombatant?.name,
          targetType: mark.targetType || targetCombatant?.type || null,
          casterId: mark.casterId || char.id,
          casterName: char.name,
          source: char.name || DEFAULT_SOURCE,
          note: 'Mark',
          effectId: mark.id || mark.effectId || null,
          effectGroupId: mark.effectGroupId || null,
          index,
          runtime: {
            bonusDice: mark.bonusDice,
            casterOnly: mark.casterOnly,
            raw: mark,
          },
        }));
      });
    }
  }

  return canonicalEffects;
};

export const getRenderableEffects = (options = {}) => (
  dedupeLegacyEquippedItemEffects(getCanonicalEffects({ includeLegacy: false, ...options })).map((effect) => ({
    id: effect.effectId,
    effectId: effect.effectId,
    effectInstanceId: effect.effectInstanceId || null,
    effectGroupId: effect.effectGroupId,
    category: effect.category,
    name: effect.display?.label || effect.text,
    text: effect.text,
    legacyKey: effect.legacyKey || null,
    occurrence: effect.occurrence ?? null,
    listKey: effect.listKey || null,
    listIndex: effect.listIndex ?? null,
    permanence: effect.permanence || 'temporary',
    isDisabled: effect.isDisabled === true,
    isHidden: effect.isHidden === true || effect.display?.hidden === true,
    source: effect.source || null,
    note: effect.note || null,
    playerTooltip: effect.display?.playerTooltip || '',
    colorId: effect.display?.colorId || null,
    noteVisible: effect.display?.noteVisible !== false,
    targetId: effect.targetId,
    targetName: effect.targetName,
    targetType: effect.targetType,
    casterName: effect.casterName,
    casterId: effect.casterId,
    duration: effect.duration,
    remaining: effect.remaining,
    concentration: effect.concentration,
    modifier: effect.modifier,
    autoApply: effect.autoApply,
    bonusDice: effect.bonusDice,
    percentIncrease: effect.percentIncrease,
    flatBonus: effect.flatBonus,
    dotDamage: effect.dotDamage,
    dotDamageType: effect.dotDamageType,
    hotHeal: effect.hotHeal,
    hotTempHp: effect.hotTempHp,
    saveType: effect.saveType,
    saveDC: effect.saveDC,
    saveTiming: effect.saveTiming,
    saveRemovesEffect: effect.saveRemovesEffect,
    casterOnly: effect.casterOnly,
    sourceType: effect.sourceType,
    storageSourceType: effect.storageSourceType || null,
    provenance: effect.provenance || null,
    itemEntryId: effect.itemEntryId || null,
    itemId: effect.itemId || null,
    itemDefinitionId: effect.itemDefinitionId || null,
    itemName: effect.itemName || null,
    abilityId: effect.abilityId || null,
    abilityName: effect.abilityName || null,
    sourceMode: effect.sourceMode || null,
  }))
);

export const buildLegacyEffectCollections = (options = {}) => {
  const effects = buildLegacyEffectEntries(options);
  const activeEffects = dedupeLegacyEquippedItemEffects(effects.filter((effect) => !effect.isDisabled));
  const buffSources = {};

  activeEffects.forEach((effect) => {
    buffSources[effect.text] = effect.source;
  });

  return {
    effects,
    activeEffects,
    permBuffs: activeEffects.filter((effect) => effect.listKey === 'permBuffs').map((effect) => effect.text),
    buffList: activeEffects.filter((effect) => effect.listKey === 'buffList').map((effect) => effect.text),
    permDebuffs: activeEffects.filter((effect) => effect.listKey === 'permDebuffs').map((effect) => effect.text),
    debuffList: activeEffects.filter((effect) => effect.listKey === 'debuffList').map((effect) => effect.text),
    buffSources,
  };
};
